import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { appendDateTimestamp } from '@/utils/appendDateTimestamp';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type AdjustmentInvoiceRequestBody =
  paths['/adjustment-invoices']['post']['requestBody']['content']['application/json'];
export type AdjustmentInvoiceResponse =
  paths['/adjustment-invoices']['post']['responses']['200']['content']['application/json'];
export type AdjustmentInvoiceFetchOptions = paths['/adjustment-invoices']['post']['parameters']['query'];
export type AdjustmentInvoiceSearchFilters = AdjustmentInvoiceRequestBody['search'];
export type AdjustmentInvoice = AdjustmentInvoiceResponse['data']['invoices'][0];

interface AdjustmentInvoiceParams {
  makerOrganizationUuid: string;
  options?: AdjustmentInvoiceFetchOptions;
  search?: AdjustmentInvoiceSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchAdjustmentInvoices = async ({
  makerOrganizationUuid,
  options,
  search,
  takerMarkets,
}: AdjustmentInvoiceParams): Promise<AdjustmentInvoiceResponse['data']> => {
  const limit = options?.limit ?? 100;
  const params = qs.stringify({ ...options, limit }, { addQueryPrefix: true });

  const response = await apiClient
    .post(`api/supplier-experience/adjustment-invoices${params}`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((takerMarket) => ({
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies AdjustmentInvoiceRequestBody,
    })
    .json<AdjustmentInvoiceResponse>();

  return {
    ...response.data,
    invoices: response.data.invoices.map((invoice) => ({
      ...invoice,
      originalDepositDate: invoice.originalDepositDate ? appendDateTimestamp(invoice.originalDepositDate) : null,
    })),
  };
};

const useAdjustmentInvoices = (params: AdjustmentInvoiceParams) => {
  const { makerOrganizationUuid, options, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['adjustment-invoices', makerOrganizationUuid, options, search, ...takerMarketUuidsMap]
      : ['adjustment-invoices-invalid'],
    queryFn: () => (isValid ? fetchAdjustmentInvoices(params) : null),
  });
};

export default useAdjustmentInvoices;
